import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { inclusions } from '../api/endpoints.js';
import { type InclusionDetail } from '../api/schemas.js';

export function InclusionDetailPage(): React.JSX.Element {
  const id = useParams().inclusionId ?? '';
  const query = inclusions.useItem(id);
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const remove = inclusions.useSoftDelete();

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.INCLUSION_READ}
      parent={{ to: '/inclusions', label: 'Inclusions' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.INCLUSION_UPDATE}>
            <Button asChild variant="outline">
              <Link to={`/inclusions/${id}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.INCLUSION_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data: InclusionDetail) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete inclusion"
            description={`${data.name} will be retired from the catalogue. Existing rate plans and history are retained.`}
            confirmLabel="Delete"
            destructive
            loading={remove.isPending}
            onConfirm={() => {
              void remove.mutateAsync(id).then(() => {
                setConfirmingDelete(false);
                void navigate('/inclusions');
              });
            }}
          />
          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Currency">{data.currency}</DetailField>
            <DetailField label="Unit cost">{data.unitCost}</DetailField>
            <DetailField label="Requires fulfillment">
              {data.requiresFulfillment ? 'Yes' : 'No'}
            </DetailField>
            <DetailField label="Active">{data.isActive ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Fulfillment department">{data.fulfillmentDepartment}</DetailField>
            <DetailField label="Description">{data.description}</DetailField>
            <DetailField label="Default instructions">{data.defaultInstructions}</DetailField>
            <DetailField label="Version">{data.version}</DetailField>
          </DetailGrid>
        </>
      )}
    </ResourceDetailPage>
  );
}
